import React, { useEffect } from "react";
import { BrowserRouter as Router, Redirect, Route, Switch, useLocation, useHistory } from "react-router-dom";
import AuthPage from "./Pages/Auth/AuthPage.jsx";
import AppVersionProvider from "./Providers/Common/AppVersionProvider.jsx";
import Subjects from "./Pages/Subjects/Subjects.jsx";
import DialogsProvider from "./Providers/Common/DialogsProvider.jsx";
import AuthProvider from "./Providers/Auth/AuthProvider.jsx";
import I18nProvider from "./Providers/i18n/I18nProvider.jsx";
import Home from "./Pages/Home/Home.jsx";
import AuthProtectedRoute from "./Providers/Auth/AuthProtectedRoute.jsx";
import BaseDataProvider from "./Providers/Data/BaseDataProvider.jsx";
import UserDataProtectedRoute from "./Providers/Data/UserDataProtectedRoute.jsx";
import UserPreferencesProvider from "./Providers/Data/UserPreferencesProvider.jsx";
import HttpProvider from "./Providers/Common/HttpProvider.jsx";
import CompanyProtectedRoute from "./Providers/Data/CompanyProtectedRoute.jsx";
import PayWebview from "./Pages/Payments/PayWebview.jsx";
import MyScrollbars from "./SharedComponents/MyScrollbars.jsx";
import { IS_COMMON_APP } from "./constants.js";
import Offer from "./Pages/Offer/Offer.jsx";
import SubjectDetails from "./Pages/Subjects/SubjectDetails.jsx";
import PaymentProvider from "./Providers/Payment/PaymentProvider.jsx";
import LoginKeyHandler from "./Providers/Auth/LoginKeyHandler.jsx";
import ChatProvider, { useChat } from "./Providers/Data/ChatProvider.jsx";
import EventsProvider from "./Providers/Data/EventsProvider.jsx";
import NewsProvider from "./Providers/Data/NewsProvider.jsx";
import AnalyticsRouterListener from "./Analytics/AnalyticsRouterListener.jsx";
import SubjectLearnRedirect from "./Routes/SubjectLearnRedirect.jsx";
import PolicyPage from "./Pages/Subjects/PolicyPage.jsx";

/** Для проброса данных Router-а (например, location) верхним провайдерам */
const RouterExposer = () => {
  const location = useLocation();
  const history = useHistory();
  const { setLocation, setHistory } = useChat();

  // Проброс location в чат-провайдер
  useEffect(() => {
    setLocation(location);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // Проброс history в чат-провайдер
  useEffect(() => {
    setHistory(history);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  return null;
};

/** */
function App() {
  return (
    <UserPreferencesProvider>
      <I18nProvider>
        <AppVersionProvider>
          <DialogsProvider>
            <HttpProvider>
              <AuthProvider>
                <BaseDataProvider>
                  <ChatProvider>
                    <EventsProvider>
                      <MyScrollbars style={{ height: "100vh" }}>
                        <Router>
                          <AnalyticsRouterListener />
                          <RouterExposer />
                          <PaymentProvider>
                            <NewsProvider>
                              <Switch>
                                <Route
                                  exact
                                  path={[
                                    "/:companyName/login",
                                    "/login",
                                    "/learn/:deckId/login",
                                    "/:companyName/learn/:deckId/login",
                                    "/events/:eventId/login",
                                    "/:companyName/events/:eventId/login",
                                  ]}
                                >
                                  <LoginKeyHandler />
                                </Route>
                                <Route
                                  exact
                                  path={["/:subjectId/learn/:deckId", "/:companyName/:subjectId/learn/:deckId"]}
                                >
                                  <SubjectLearnRedirect />
                                </Route>
                                <Route exact path={["/offer/:urn/:offerId", "/:companyName/offer/:urn/:offerId"]}>
                                  <Offer />
                                </Route>
                                <UserDataProtectedRoute exact path="/">
                                  <Home />
                                </UserDataProtectedRoute>
                                <UserDataProtectedRoute
                                  path={[
                                    "/dictionary",
                                    "/homeworks",
                                    "/events",
                                    "/news",
                                    "/learn",
                                    "/missions",
                                    "/chat",
                                    "/leaderboard",
                                    "/branches",
                                    "/decks",
                                  ]}
                                >
                                  <Home />
                                </UserDataProtectedRoute>
                                <Route exact path={["/auth", "/password", "/registration"]}>
                                  <AuthPage />
                                </Route>
                                <AuthProtectedRoute exact path="/subjects">
                                  <Subjects />
                                </AuthProtectedRoute>
                                <AuthProtectedRoute exact path="/subjects/:subjectId">
                                  <SubjectDetails />
                                </AuthProtectedRoute>
                                <Route exact path={["/offer/:urn/:offerId", "/:companyName/offer/:urn/:offerId"]}>
                                  <Offer />
                                </Route>
                                <AuthProtectedRoute exact path={["/pay", "/:companyName/pay"]}>
                                  <PayWebview />
                                </AuthProtectedRoute>
                                <CompanyProtectedRoute exact path="/subjects">
                                  <Subjects />
                                </CompanyProtectedRoute>
                                <CompanyProtectedRoute exact path="/subjects/:subjectId">
                                  <SubjectDetails />
                                </CompanyProtectedRoute>
                                <AuthProtectedRoute exact path="/profile">
                                  <Home />
                                </AuthProtectedRoute>
                                <AuthProtectedRoute exact path="/orders">
                                  <Home />
                                </AuthProtectedRoute>
                                {IS_COMMON_APP && (
                                  <>
                                    <CompanyProtectedRoute exact path="/:companyName">
                                      <Subjects />
                                    </CompanyProtectedRoute>
                                    <CompanyProtectedRoute exact path="/:companyName/subjects/:subjectId">
                                      <SubjectDetails />
                                    </CompanyProtectedRoute>
                                    <Route
                                      exact
                                      path={[
                                        "/:companyName/auth",
                                        "/:companyName/password",
                                        "/:companyName/registration",
                                      ]}
                                    >
                                      <AuthPage />
                                    </Route>
                                    <AuthProtectedRoute exact path="/:companyName/pay">
                                      <PayWebview />
                                    </AuthProtectedRoute>
                                    <Route exact path={["/:companyName/terms", "/:companyName/policy"]}>
                                      <PolicyPage />
                                    </Route>
                                  </>
                                )}
                                <Redirect to="/" />
                              </Switch>
                            </NewsProvider>
                          </PaymentProvider>
                        </Router>
                      </MyScrollbars>
                    </EventsProvider>
                  </ChatProvider>
                </BaseDataProvider>
              </AuthProvider>
            </HttpProvider>
          </DialogsProvider>
        </AppVersionProvider>
      </I18nProvider>
    </UserPreferencesProvider>
  );
}

export default App;
